import * as faceLandmarksDetection from "@tensorflow-models/face-landmarks-detection";
import * as tf from "@tensorflow/tfjs";
import { Camera, SquareUserRound } from "lucide-react";
import React from "react";
import Webcam from "react-webcam";
import LoadingScreen from "./common/LoadingScreen";

class OwnPhotoUpload extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      model: null,
      imageUrl: null,
      detectionStatus: "Initializing face detection...",
      modelLoading: true,
      modelError: null,
      detecting: false,
      faceDetected: false,
      livenessSteps: [],
      currentStepIndex: 0,
      completedSteps: 0,
      totalSteps: 4,
      blinkDetected: false,
      smileDetected: false,
      leftMoveDetected: false,
      rightMoveDetected: false,
      currentInstructionEn: "",
      currentInstructionBn: "",
      humanConfidence: 0,
      isProcessing: false,
      isLoading: false,
      error: null,
    };

    this.webcamRef = React.createRef();
    this.isDetecting = false;
    this.detectionInterval = null;
    this.stepDetectionInterval = null;
    this.modelInitialized = false;
    this.workingVideoConstraints = null;

    this.DETECTION_INTERVAL = 100;
    this.BLINK_THRESHOLD = 0.25;
    this.SMILE_THRESHOLD = 0.08;
    this.MOVEMENT_THRESHOLD = 25;

    this.blinkState = null;
    this.smileState = null;
    this.movementState = null;

    this.LIVENESS_ACTIONS = {
      blink: {
        en: "Please blink your eyes slowly and clearly 3 times (one after another)",
        bn: "অনুগ্রহ করে ধীরে ধীরে এবং স্পষ্টভাবে ৩ বার চোখের পলক দিন (একের পর এক)",
      },
      smile: {
        en: "Please smile naturally and hold for 2 seconds",
        bn: "অনুগ্রহ করে স্বাভাবিকভাবে হাসুন এবং ২ সেকেন্ড ধরে রাখুন",
      },
      left: {
        en: "Please turn your face CLEARLY to the left and hold for 1 second",
        bn: "অনুগ্রহ করে আপনার মুখ স্পষ্টভাবে বামে ঘুরান এবং ১ সেকেন্ড ধরে রাখুন",
      },
      right: {
        en: "Please turn your face CLEARLY to the right and hold for 1 second",
        bn: "অনুগ্রহ করে আপনার মুখ স্পষ্টভাবে ডানে ঘুরান এবং ১ সেকেন্ড ধরে রাখুন",
      },
    };
  }

  async componentDidMount() {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.setState({
          modelError:
            "Camera access not supported in this browser. Please use Chrome, Firefox, or Safari.",
          modelLoading: false,
        });
        return;
      }

      if (
        window.location.protocol !== "https:" &&
        window.location.hostname !== "localhost"
      ) {
        this.setState({
          modelError: "Camera access requires secure connection (HTTPS).",
          modelLoading: false,
        });
        return;
      }

      await this.initializeTensorFlow();
    } catch (error) {
      console.error("Component initialization failed:", error);
      this.setState({
        modelError: `Initialization failed: ${error.message}`,
        modelLoading: false,
      });
    }
  }

  componentWillUnmount() {
    this.cleanup();
  }

  async initializeTensorFlow() {
    try {
      let backendSet = false;
      const backends = ["webgl", "cpu"];

      for (const backend of backends) {
        try {
          await tf.setBackend(backend);
          await tf.ready();
          backendSet = true;
          break;
        } catch (backendError) {
          console.warn(`${backend} backend failed:`, backendError.message);
          continue;
        }
      }

      if (!backendSet) {
        throw new Error("No compatible TensorFlow backend available");
      }

      this.setState({ detectionStatus: "Loading model..." });
      await this.loadModel();
    } catch (error) {
      console.error("TensorFlow initialization failed:", error);
      this.setState({
        modelError: `Initialization failed: ${error.message}`,
        modelLoading: false,
      });
    }
  }

  async loadModel() {
    try {
      const localModelPath = "./models/face-mesh-model/model.json";

      const modelConfig = {
        runtime: "tfjs",
        maxFaces: 1,
        refineLandmarks: false,
        staticImageMode: false,
        modelUrl: localModelPath,
      };

      const model = await faceLandmarksDetection.createDetector(
        faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh,
        modelConfig,
      );

      this.modelInitialized = true;
      this.setState({
        model,
        modelLoading: false,
        detectionStatus: "Ready! Click 'Start Liveness Verification'.",
      });
    } catch (error) {
      console.error("Model loading error:", error);
      this.setState({
        modelError: `Model loading failed: ${error.message}`,
        modelLoading: false,
      });
    }
  }

  requestCameraPermission = async () => {
    try {
      if (!window.isSecureContext) {
        throw new Error(
          "Camera access requires a secure context (HTTPS or localhost)",
        );
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const legacyAPI =
          navigator.getUserMedia ||
          navigator.webkitGetUserMedia ||
          navigator.mozGetUserMedia ||
          navigator.msGetUserMedia;

        if (legacyAPI) {
          navigator.mediaDevices = {
            getUserMedia: async (constraints) => {
              return new Promise((resolve, reject) => {
                legacyAPI.call(navigator, constraints, resolve, reject);
              });
            },
          };
        } else {
          throw new Error("Camera access not supported in this browser");
        }
      }

      this.setState({
        detectionStatus: "Requesting camera permission...",
      });

      const constraintSets = [
        {
          video: { facingMode: "user" },
          audio: false,
        },
        {
          video: {
            width: { ideal: 320, min: 240, max: 640 },
            height: { ideal: 240, min: 180, max: 480 },
            facingMode: { ideal: "user" },
            frameRate: { ideal: 12, min: 8, max: 30 },
          },
          audio: false,
        },
        {
          video: {
            width: { ideal: 320 },
            height: { ideal: 240 },
            facingMode: { ideal: "user" },
          },
          audio: false,
        },
        {
          video: true,
          audio: false,
        },
      ];

      for (let i = 0; i < constraintSets.length; i++) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(
            constraintSets[i],
          );
          this.workingVideoConstraints = constraintSets[i].video;
          stream.getTracks().forEach((track) => track.stop());
          return true;
        } catch (constraintError) {
          console.warn(`Constraint set ${i + 1} failed:`, constraintError.name);
          if (constraintError.name === "NotAllowedError") {
            throw constraintError;
          }
          continue;
        }
      }

      throw new Error("No compatible camera constraints found");
    } catch (error) {
      console.error("Camera permission error:", error);
      this.handleCameraPermissionError(error);
      return false;
    }
  };

  handleCameraPermissionError = (error) => {
    let errorMessage = "";
    let actionButtons = [];

    switch (error.name) {
      case "NotAllowedError":
      case "PermissionDeniedError":
        errorMessage =
          "Camera access was denied. Please allow camera access to continue.";
        actionButtons = [
          {
            label: "Try Again",
            onClick: () => this.retryWithPermission(),
          },
        ];
        break;
      case "NotReadableError":
      case "TrackStartError":
        errorMessage =
          "Could not start video stream. Another application might be using your camera.";
        actionButtons = [
          {
            label: "Try Again",
            onClick: () => this.retryWithPermission(),
          },
        ];
        break;
      case "NotFoundError":
      case "DevicesNotFoundError":
        errorMessage =
          "No camera found on your device. Please ensure a camera is connected.";
        actionButtons = [
          {
            label: "Retry",
            onClick: () => this.retryWithPermission(),
          },
        ];
        break;
      default:
        errorMessage = `Camera access failed: ${error.message}. Please check your browser settings.`;
        actionButtons = [
          {
            label: "Try Again",
            onClick: () => this.retryWithPermission(),
          },
        ];
    }

    this.setState({
      detectionStatus: "Camera access failed",
      modelError: errorMessage,
    });

    alert(errorMessage);
  };

  retryWithPermission = async () => {
    this.setState({
      modelError: null,
      detectionStatus: "Retrying camera access...",
    });

    await new Promise((resolve) => setTimeout(resolve, 500));

    const hasPermission = await this.requestCameraPermission();

    if (hasPermission) {
      this.setState({
        detectionStatus: "Camera permission granted! Starting verification...",
        isOpen: false,
      });

      setTimeout(() => {
        this.handleClick();
      }, 1000);
    }
  };

  cleanup() {
    this.stopDetection();
    if (this.detectionInterval) {
      clearInterval(this.detectionInterval);
      this.detectionInterval = null;
    }
    if (this.stepDetectionInterval) {
      clearInterval(this.stepDetectionInterval);
      this.stepDetectionInterval = null;
    }
    if (this.state.model) {
      try {
        this.state.model.dispose();
      } catch (error) {
        console.warn("Error disposing model:", error);
      }
    }
  }

  generateRandomLivenessSequence() {
    const firstThreeActions = ["blink", "left", "right"];
    const shuffledFirstThree = [...firstThreeActions].sort(
      () => Math.random() - 0.5,
    );
    const finalSequence = [...shuffledFirstThree, "smile"];
    return finalSequence;
  }

  updateInstruction(instructionEn, instructionBn) {
    this.setState({
      currentInstructionEn: instructionEn,
      currentInstructionBn: instructionBn,
    });
  }

  startFaceDetection() {
    this.isDetecting = true;
    this.setState({ detectionStatus: "Looking for your face..." });

    this.detectionInterval = setInterval(() => {
      if (this.isDetecting) {
        this.detectFace();
      }
    }, this.DETECTION_INTERVAL);
  }

  stopDetection() {
    this.isDetecting = false;
    if (this.detectionInterval) {
      clearInterval(this.detectionInterval);
      this.detectionInterval = null;
    }
    if (this.stepDetectionInterval) {
      clearInterval(this.stepDetectionInterval);
      this.stepDetectionInterval = null;
    }
  }

  async detectFace() {
    if (!this.isDetecting || !this.state.isOpen || this.state.imageUrl) return;

    const { model } = this.state;
    const video = this.webcamRef?.current?.video;

    if (!model || !video || video.readyState < 2) {
      this.handleFaceDetected();
      return;
    }

    try {
      await model.estimateFaces(video, {
        flipHorizontal: false,
        staticImageMode: false,
        maxFaces: 1,
        refineLandmarks: false,
      });
      this.handleFaceDetected();
    } catch (error) {
      this.handleFaceDetected();
    }
  }

  handleFaceDetected() {
    if (!this.state.faceDetected) {
      this.setState({
        faceDetected: true,
        detectionStatus: "Ready! Starting liveness verification...",
        humanConfidence: 0,
      });

      setTimeout(() => this.initializeCurrentStep(), 1000);
    }
  }

  handleStepCompletion = () => {
    if (this.stepDetectionInterval) {
      clearInterval(this.stepDetectionInterval);
      this.stepDetectionInterval = null;
    }

    this.blinkState = null;
    this.smileState = null;
    this.movementState = null;

    const newCompletedSteps = this.state.completedSteps + 1;

    if (newCompletedSteps >= this.state.totalSteps) {
      this.setState({
        completedSteps: newCompletedSteps,
        detectionStatus: "Verification complete! Taking photo...",
      });

      this.updateInstruction(
        "Perfect! All steps completed.",
        "চমৎকার! সব ধাপ সম্পন্ন।",
      );

      setTimeout(() => {
        if (this.webcamRef.current) {
          const imageSrc = this.webcamRef.current.getScreenshot();
          this.setState({
            imageUrl: imageSrc,
            detectionStatus: "Photo captured successfully!",
          });
          this.stopDetection();
        }
      }, 1000);
    } else {
      const nextStepIndex = this.state.currentStepIndex + 1;
      this.setState(
        {
          completedSteps: newCompletedSteps,
          currentStepIndex: nextStepIndex,
        },
        () => {
          setTimeout(() => this.initializeCurrentStep(), 1500);
        },
      );
    }
  };

  initializeCurrentStep() {
    const currentAction = this.state.livenessSteps[this.state.currentStepIndex];
    const instruction = this.LIVENESS_ACTIONS[currentAction];

    if (!instruction) {
      console.error("Invalid action:", currentAction);
      return;
    }

    this.setState({
      currentInstructionEn: instruction.en,
      currentInstructionBn: instruction.bn,
      blinkDetected: false,
      smileDetected: false,
      leftMoveDetected: false,
      rightMoveDetected: false,
    });

    this.resetBlinkDetection();
    this.startStepDetection(currentAction);
  }

  resetBlinkDetection = () => {
    this.blinkState = null;
  };

  startStepDetection(action) {
    this.stepDetectionInterval = setInterval(() => {
      this.performDetection(action);
    }, 300);
  }

  performDetection = async (action) => {
    if (!this.isDetecting || !this.state.isOpen) return;

    const { model } = this.state;
    const video = this.webcamRef?.current?.video;

    if (!model || !video || video.readyState < 2) {
      return;
    }

    if (this.state.isProcessing) {
      return;
    }

    this.setState({ isProcessing: true });

    try {
      const detectionConfig = {
        flipHorizontal: false,
        maxFaces: 1,
        staticImageMode: true,
        refineLandmarks: false,
      };

      const predictions = await model.estimateFaces(video, detectionConfig);

      if (Array.isArray(predictions) && predictions.length > 0) {
        const face = predictions[0];
        const keypoints = face.keypoints;

        if (keypoints && keypoints.length >= 100) {
          const detectionMethods = {
            blink: () => this.detectBlink(keypoints),
            smile: () => this.detectSmile(keypoints),
            left: () => this.detectMovement(keypoints, "left"),
            right: () => this.detectMovement(keypoints, "right"),
          };

          if (detectionMethods[action]) {
            detectionMethods[action]();
          }
        }
      }
    } catch (error) {
      console.error("Face detection error:", error);
    } finally {
      this.setState({ isProcessing: false });
    }
  };

  detectBlink = (keypoints) => {
    if (this.state.blinkDetected) return;

    const earData = this.calculateEAR(keypoints);
    if (earData === null) return;

    if (!this.blinkState) {
      this.blinkState = {
        earBaseline: earData,
        consecutiveLowEAR: 0,
        blinkInProgress: false,
        openEyeFrames: 0,
        blinkCount: 0,
        lastBlinkTime: 0,
        blinkCooldown: false,
        isCalibrated: false,
        calibrationFrames: 0,
        earHistory: [],
      };

      this.updateInstruction(
        "Calibrating blink detection... keep your eyes open normally",
        "পলক সনাক্তকরণ ক্যালিব্রেট করা হচ্ছে... স্বাভাবিকভাবে চোখ খোলা রাখুন",
      );

      this.MIN_CLOSED_FRAMES = 1;
      this.MAX_CLOSED_FRAMES = 15;
      this.BLINK_THRESHOLD = 0.8;
      this.BLINK_COOLDOWN_TIME = 800;

      return;
    }

    if (!this.blinkState.isCalibrated) {
      this.blinkState.calibrationFrames++;
      this.blinkState.earHistory.push(earData);

      if (this.blinkState.earHistory.length > 20) {
        this.blinkState.earHistory.shift();
      }

      if (this.blinkState.calibrationFrames >= 25) {
        const recentReadings = this.blinkState.earHistory.slice(-15);
        this.blinkState.earBaseline =
          recentReadings.reduce((a, b) => a + b, 0) / recentReadings.length;
        this.blinkState.isCalibrated = true;

        this.updateInstruction(
          "Ready! Now blink your eyes slowly and clearly 3 times",
          "প্রস্তুত! এখন আবার ধীরে ধীরে এবং স্পষ্টভাবে চোখের পলক দিন",
        );
      }
      return;
    }

    if (this.blinkState.blinkCooldown) {
      return;
    }

    const blinkThreshold = this.blinkState.earBaseline * this.BLINK_THRESHOLD;
    const isEyesClosed = earData < blinkThreshold;

    if (isEyesClosed) {
      const newCount = this.blinkState.consecutiveLowEAR + 1;
      this.blinkState.consecutiveLowEAR = newCount;
      this.blinkState.blinkInProgress = true;
      this.blinkState.openEyeFrames = 0;
    } else {
      const openFrames = this.blinkState.openEyeFrames + 1;
      this.blinkState.openEyeFrames = openFrames;

      if (
        this.blinkState.blinkInProgress &&
        this.blinkState.consecutiveLowEAR >= this.MIN_CLOSED_FRAMES &&
        this.blinkState.consecutiveLowEAR <= this.MAX_CLOSED_FRAMES &&
        openFrames >= 1
      ) {
        this.handleValidBlink();
      }

      if (openFrames >= 1) {
        this.blinkState.consecutiveLowEAR = 0;
        this.blinkState.blinkInProgress = false;
      }
    }
  };

  handleValidBlink = () => {
    const currentTime = Date.now();

    if (
      currentTime - this.blinkState.lastBlinkTime <
      this.BLINK_COOLDOWN_TIME
    ) {
      return;
    }

    this.blinkState.lastBlinkTime = currentTime;
    this.blinkState.blinkCooldown = true;

    setTimeout(() => {
      if (this.blinkState) {
        this.blinkState.blinkCooldown = false;
      }
    }, this.BLINK_COOLDOWN_TIME);

    this.blinkState.blinkCount++;

    this.updateInstruction(
      `Excellent blink! ${
        3 - this.blinkState.blinkCount
      } more needed, blink your eyes slowly and clearly `,
      `চমৎকার পলক! আরো ${
        3 - this.blinkState.blinkCount
      }টি প্রয়োজন, আবার ধীরে ধীরে এবং স্পষ্টভাবে চোখের পলক দিন`,
    );

    if (this.blinkState.blinkCount >= 3) {
      this.setState({
        blinkDetected: true,
        humanConfidence: this.state.humanConfidence + 0.25,
      });
      this.updateInstruction(
        "Perfect! All blinks completed!",
        "নিখুঁত! সব পলক সম্পন্ন!",
      );
      setTimeout(() => this.handleStepCompletion(), 1000);
    }
  };

  calculateEAR = (keypoints) => {
    try {
      const leftEyeTop = keypoints[159];
      const leftEyeBottom = keypoints[145];
      const leftEyeLeft = keypoints[33];
      const leftEyeRight = keypoints[133];

      const rightEyeTop = keypoints[386];
      const rightEyeBottom = keypoints[374];
      const rightEyeLeft = keypoints[362];
      const rightEyeRight = keypoints[263];

      if (
        !leftEyeTop ||
        !leftEyeBottom ||
        !leftEyeLeft ||
        !leftEyeRight ||
        !rightEyeTop ||
        !rightEyeBottom ||
        !rightEyeLeft ||
        !rightEyeRight
      ) {
        return null;
      }

      const leftEyeHeight = Math.abs(leftEyeTop.y - leftEyeBottom.y);
      const leftEyeWidth = Math.abs(leftEyeLeft.x - leftEyeRight.x);
      const leftEAR = leftEyeWidth > 0 ? leftEyeHeight / leftEyeWidth : 0;

      const rightEyeHeight = Math.abs(rightEyeTop.y - rightEyeBottom.y);
      const rightEyeWidth = Math.abs(rightEyeLeft.x - rightEyeRight.x);
      const rightEAR = rightEyeWidth > 0 ? rightEyeHeight / rightEyeWidth : 0;

      const avgEAR = (leftEAR + rightEAR) / 2;

      if (avgEAR < 0.02 || avgEAR > 0.8 || isNaN(avgEAR)) {
        return null;
      }

      return avgEAR;
    } catch (error) {
      console.error("BLINK: EAR calculation error:", error);
      return null;
    }
  };

  detectMovement = (keypoints, direction) => {
    const detectedKey = `${direction}MoveDetected`;
    if (this.state[detectedKey]) return;

    const facePoints = [
      keypoints[1],
      keypoints[2],
      keypoints[5],
      keypoints[6],
      keypoints[10],
    ].filter((point) => point != null);

    if (facePoints.length === 0) {
      return;
    }

    const facePoint = facePoints[0];

    if (!this.movementState) {
      this.movementState = {
        centerX: facePoint.x,
        startTime: Date.now(),
        requiredDuration: 1000,
        minMovement: 30,
        isInPosition: false,
        positionStartTime: 0,
      };
    }

    const movement = facePoint.x - this.movementState.centerX;
    const currentTime = Date.now();

    let inTargetPosition = false;
    if (direction === "left" && movement > this.movementState.minMovement) {
      inTargetPosition = true;
    } else if (
      direction === "right" &&
      movement < -this.movementState.minMovement
    ) {
      inTargetPosition = true;
    }

    if (inTargetPosition && !this.movementState.isInPosition) {
      this.movementState.isInPosition = true;
      this.movementState.positionStartTime = currentTime;
      this.updateInstruction(
        `Good ${direction} turn! Hold position...`,
        `ভালো ${
          direction === "left" ? "বাম" : "ডান"
        } ঘুরানো! অবস্থান ধরে রাখুন...`,
      );
    }

    if (this.movementState.isInPosition) {
      if (inTargetPosition) {
        const timeHeld = currentTime - this.movementState.positionStartTime;
        const progress = Math.min(
          (timeHeld / this.movementState.requiredDuration) * 100,
          100,
        );

        this.updateInstruction(
          `Keep holding ${direction}... ${progress.toFixed(0)}%`,
          `${
            direction === "left" ? "বাম" : "ডান"
          } ধরে রাখুন... ${progress.toFixed(0)}%`,
        );

        if (timeHeld >= this.movementState.requiredDuration) {
          this.setState({
            [detectedKey]: true,
            humanConfidence: this.state.humanConfidence + 0.25,
          });
          this.updateInstruction(
            `Perfect ${direction} turn completed!`,
            `চমৎকার ${direction === "left" ? "বাম" : "ডান"} ঘুরানো সম্পন্ন!`,
          );
          setTimeout(() => this.handleStepCompletion(), 1000);
        }
      } else {
        this.movementState.isInPosition = false;
        this.updateInstruction(
          `Position lost! Turn ${direction} again`,
          `অবস্থান হারিয়ে গেছে! আবার ${
            direction === "left" ? "বামে" : "ডানে"
          } ঘুরান`,
        );
      }
    } else {
      const currentMovement = Math.abs(movement);
      const needed = this.movementState.minMovement;
      const progressPercent = Math.min((currentMovement / needed) * 100, 100);

      if (direction === "left") {
        if (movement > 0 && movement < needed) {
          this.updateInstruction(
            `Turn left more (${progressPercent.toFixed(0)}% completed)`,
            `আরো বামে ঘুরান (${progressPercent.toFixed(0)}% সম্পন্ন)`,
          );
        } else {
          this.updateInstruction(
            `Please turn your head to the left`,
            `মাথা বামে ঘুরান`,
          );
        }
      } else {
        if (movement < 0 && Math.abs(movement) < needed) {
          this.updateInstruction(
            `Turn right more (${progressPercent.toFixed(0)}% completed)`,
            `আরো ডানে ঘুরান (${progressPercent.toFixed(0)}% সম্পন্ন)`,
          );
        } else {
          this.updateInstruction(
            `Please turn your head to the right`,
            `মাথা ডানে ঘুরান`,
          );
        }
      }
    }
  };

  detectSmile = (keypoints) => {
    if (this.state.smileDetected) return;

    const smileData = this.calculateSmile(keypoints);
    if (!smileData) return;

    if (!this.smileState) {
      this.smileState = {
        baseline: smileData.intensity,
        isSmiling: false,
        smileStartTime: 0,
        requiredDuration: 2000,
      };
    }

    const smileIntensity = smileData.intensity - this.smileState.baseline;
    const isSmiling = smileIntensity > 0.15;
    const currentTime = Date.now();

    if (isSmiling && !this.smileState.isSmiling) {
      this.smileState.isSmiling = true;
      this.smileState.smileStartTime = currentTime;
      this.updateInstruction(
        "Great smile! Hold it for 2 seconds...",
        "চমৎকার হাসি! ২ সেকেন্ড ধরে রাখুন...",
      );
    }

    if (this.smileState.isSmiling) {
      const duration = currentTime - this.smileState.smileStartTime;
      const progress = Math.min(
        (duration / this.smileState.requiredDuration) * 100,
        100,
      );

      if (isSmiling) {
        this.updateInstruction(
          `Keep smiling... ${progress.toFixed(0)}%`,
          `হাসি ধরে রাখুন... ${progress.toFixed(0)}%`,
        );

        if (duration >= this.smileState.requiredDuration) {
          this.setState({
            smileDetected: true,
            humanConfidence: this.state.humanConfidence + 0.25,
          });
          this.updateInstruction(
            "Perfect smile captured!",
            "নিখুঁত হাসি ধরা হয়েছে!",
          );
          setTimeout(() => this.handleStepCompletion(), 800);
        }
      } else {
        this.smileState.isSmiling = false;
        this.updateInstruction(
          "Smile lost! Please smile clearly and hold it",
          "হাসি চলে গেছে! স্পষ্ট হাসি দিয়ে ধরে রাখুন",
        );
      }
    } else {
      this.updateInstruction(
        "Please smile clearly and naturally",
        "স্পষ্ট এবং স্বাভাবিক হাসি দিন",
      );
    }
  };

  calculateSmile = (keypoints) => {
    try {
      const leftCorner = keypoints[61];
      const rightCorner = keypoints[291];
      const topLip = keypoints[13];
      const bottomLip = keypoints[14];

      if (!leftCorner || !rightCorner || !topLip || !bottomLip) return null;

      const mouthWidth = Math.abs(rightCorner.x - leftCorner.x);

      const leftFace = keypoints[127];
      const rightFace = keypoints[356];
      const faceWidth =
        leftFace && rightFace ? Math.abs(rightFace.x - leftFace.x) : 100;

      const normalizedMouthWidth = mouthWidth / faceWidth;

      const avgCornerY = (leftCorner.y + rightCorner.y) / 2;
      const lipCenterY = (topLip.y + bottomLip.y) / 2;
      const cornerElevation = Math.max(
        0,
        (lipCenterY - avgCornerY) / faceWidth,
      );

      const widthComponent = Math.max(0, normalizedMouthWidth - 0.04) * 3;
      const elevationComponent = cornerElevation * 15;
      const smileIntensity = widthComponent + elevationComponent;

      return {
        intensity: smileIntensity,
        width: normalizedMouthWidth,
        elevation: cornerElevation,
      };
    } catch (error) {
      return null;
    }
  };

  handleClick = () => {
    if (this.state.modelError) {
      alert("Face detection system is not ready. Please refresh the page.");
      return;
    }

    if (!this.state.isOpen) {
      this.startLivenessVerification();
    } else {
      this.stopLivenessVerification();
    }
  };

  startLivenessVerification = async () => {
    const hasPermission = await this.requestCameraPermission();
    if (!hasPermission) {
      return;
    }

    const randomSequence = this.generateRandomLivenessSequence();
    this.setState(
      {
        isOpen: true,
        livenessSteps: randomSequence,
        currentStepIndex: 0,
        completedSteps: 0,
        imageUrl: null,
        faceDetected: false,
        detecting: true,
        humanConfidence: 0,
        detectionStatus: "Starting camera...",
        currentInstructionEn: "Please wait...",
        currentInstructionBn: "অনুগ্রহ করে অপেক্ষা করুন...",
        isProcessing: false,
      },
      () => {
        setTimeout(() => this.startFaceDetection(), 1000);
      },
    );
  };

  stopLivenessVerification = () => {
    this.setState({
      isOpen: false,
      detecting: false,
      detectionStatus: "Camera stopped",
    });
    this.stopDetection();
  };

  retakePhoto = () => {
    const newSequence = this.generateRandomLivenessSequence();

    this.blinkState = null;
    this.smileState = null;
    this.movementState = null;

    this.setState(
      {
        imageUrl: null,
        faceDetected: false,
        livenessSteps: newSequence,
        currentStepIndex: 0,
        completedSteps: 0,
        humanConfidence: 0,
        blinkDetected: false,
        smileDetected: false,
        leftMoveDetected: false,
        rightMoveDetected: false,
      },
      () => {
        this.resetBlinkDetection();
        this.startFaceDetection();
      },
    );
  };

  dataSubmit = async (e) => {
    e.preventDefault();
    this.setState({ isLoading: true, error: null });

    try {
      // Prepare payload for API call (will be called later with auth token)
      const payload = {
        isFaceMatchRequired: true,
        transactionid: this.props.transactionid,
        photoBase64St: this.state.imageUrl.split(',')[1], // Remove data URL prefix to get raw base64 string
      };

      // Call the original onConfirm callback with the payload
      await this.props.onConfirm(payload);
    } catch (err) {
      console.error("Error preparing payload:", err);
      this.setState({ error: "Failed to prepare data. Please try again." });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render() {
    const videoConstraints = this.workingVideoConstraints || {
      width: { ideal: 320, min: 240, max: 640 },
      height: { ideal: 240, min: 180, max: 480 },
      facingMode: { ideal: "user" },
      frameRate: { ideal: 12, min: 8, max: 30 },
    };

    if (this.state.modelError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="w-full max-w-2xl">
            <div className="card">
              <div className="text-center">
                <h4 className="text-xl font-bold text-danger mb-3">
                  Face Detection Error
                </h4>
                <p className="text-gray-600 mb-4">{this.state.modelError}</p>
                <button
                  className="btn-primary"
                  onClick={() => window.location.reload()}
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="w-full max-w-5xl">
            <div className="card">
              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Liveness Verification
                </h2>
                <p className="text-gray-600 text-sm">
                  Complete face movements to verify your identity
                </p>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">1.</span>
                    <span className="text-gray-700">
                      Plain white background with proper lighting
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">2.</span>
                    <span className="text-gray-700">
                      No hats, glasses, or face obstructions
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">3.</span>
                    <span className="text-gray-700">
                      Complete: Blink 3×, smile, turn left, turn right
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-danger font-bold">4.</span>
                    <span className="text-gray-700">
                      Move face slowly and deliberately
                    </span>
                  </div>
                </div>
              </div>

              {/* Main Camera and Preview Section */}
              <div className="flex flex-wrap gap-4 justify-center">
                {/* Camera Section */}
                <div className="flex-1 min-w-[280px] flex flex-col items-center">
                  <div className="flex flex-col items-center justify-start p-3 w-full max-w-[350px]">
                    {this.state.isOpen ? (
                      <div className="relative flex flex-col items-center w-full mb-3">
                        <Webcam
                          audio={false}
                          height={240}
                          width={320}
                          ref={this.webcamRef}
                          screenshotFormat="image/jpeg"
                          className="border-4 border-primary h-[14rem] w-[14rem] rounded-full object-cover overflow-hidden bg-black"
                          videoConstraints={videoConstraints}
                          onUserMedia={() => {
                            this.setState({
                              detectionStatus: "Camera ready! Initializing...",
                            });
                          }}
                          onUserMediaError={this.handleCameraPermissionError}
                        />

                        {/* Camera Overlay with Visual Status */}
                        <div className="flex flex-col items-center justify-center pointer-events-none">
                          <div className="bg-black/70 rounded-lg p-1.5 text-center">
                            {/* Main Status Message */}
                            <div className="bg-black/80 px-6 py-1 rounded-lg mb-1 backdrop-blur-sm">
                              <p className="text-white m-0 mb-2.5 text-sm font-medium">
                                {this.state.detectionStatus}
                              </p>

                              {/* Progress Indicator */}
                              {this.state.faceDetected &&
                                this.state.livenessSteps.length > 0 && (
                                  <div className="w-full bg-white/20 rounded-lg h-1.5 mb-2.5">
                                    <div
                                      className="bg-secondary h-1.5 rounded-lg transition-all duration-500"
                                      style={{
                                        width: `${
                                          (this.state.completedSteps /
                                            this.state.totalSteps) *
                                          100
                                        }%`,
                                      }}
                                    ></div>
                                  </div>
                                )}
                            </div>

                            {/* Liveness Progress */}
                            {this.state.faceDetected &&
                              this.state.livenessSteps.length > 0 && (
                                <div className="mt-2 w-full max-w-[350px]">
                                  <div className="text-sm text-white font-bold bg-primary/90 p-2.5 rounded-lg text-center">
                                    {/* Step Header with Icon */}
                                    <div className="bg-primary/90 rounded-lg mb-2 flex items-center justify-center">
                                      <div className="mr-2 text-lg">
                                        {this.state.livenessSteps[
                                          this.state.currentStepIndex
                                        ] === "blink" && "👁️"}
                                        {this.state.livenessSteps[
                                          this.state.currentStepIndex
                                        ] === "smile" && "😊"}
                                        {this.state.livenessSteps[
                                          this.state.currentStepIndex
                                        ] === "left" && "⬅️"}
                                        {this.state.livenessSteps[
                                          this.state.currentStepIndex
                                        ] === "right" && "➡️"}
                                      </div>
                                      <div className="text-white font-semibold text-sm">
                                        Step {this.state.currentStepIndex + 1}{" "}
                                        of {this.state.totalSteps}:{" "}
                                        {this.state.livenessSteps[
                                          this.state.currentStepIndex
                                        ]?.toUpperCase()}
                                        {this.state.livenessSteps[
                                          this.state.currentStepIndex
                                        ] === "blink" &&
                                          this.blinkState && (
                                            <span className="text-xs ml-1 opacity-90">
                                              ({this.blinkState.blinkCount}/3
                                              blinks)
                                            </span>
                                          )}
                                      </div>
                                    </div>

                                    {/* Instruction Overlay */}
                                    <div className="bg-white/95 p-1 rounded-lg border-2 border-primary backdrop-blur-sm">
                                      <div className="text-gray-800 font-medium text-sm mb-1 text-center">
                                        {this.state.currentInstructionEn ||
                                          "Loading instructions..."}
                                      </div>
                                      <div className="text-gray-600 text-xs text-center italic">
                                        {this.state.currentInstructionBn ||
                                          "নির্দেশনা লোড হচ্ছে..."}
                                      </div>
                                    </div>

                                    {/* Step Progress Indicators */}
                                    <div className="flex justify-center mt-2 gap-2">
                                      {Array.from(
                                        { length: this.state.totalSteps },
                                        (_, index) => (
                                          <div
                                            key={index}
                                            className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                              index < this.state.completedSteps
                                                ? "bg-secondary"
                                                : index ===
                                                    this.state.currentStepIndex
                                                  ? "bg-primary scale-120"
                                                  : "bg-white/30"
                                            }`}
                                          />
                                        ),
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                            {/* Loading Spinner */}
                            {!this.state.faceDetected &&
                              this.state.detecting && (
                                <LoadingScreen
                                  message="Detecting your face..."
                                  subMessage="Please wait while we initialize face detection"
                                />
                              )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center w-full min-h-[14rem] mb-3 border-4 border-dashed border-primary rounded-full bg-gray-50">
                        <p className="border border-dashed px-3 text-center mb-2 text-sm text-gray-600">
                          {this.state.detectionStatus}
                        </p>
                        <Camera className="h-32 w-32 text-gray-400" />
                      </div>
                    )}

                    {/* Camera Control Buttons */}
                    <div className="flex flex-col gap-2 w-full max-w-[280px] mt-3 relative z-20">
                      <button
                        type="button"
                        disabled={this.state.modelLoading}
                        onClick={this.handleClick}
                        className={`w-full py-2.5 px-4 text-base font-medium text-white border-none cursor-pointer transition-all duration-200 ${
                          this.state.isOpen ? "btn-danger" : "btn-primary"
                        }`}
                      >
                        {this.state.modelLoading
                          ? "Loading..."
                          : this.state.isOpen
                            ? "Stop Webcam"
                            : "Start Liveness Verification"}
                      </button>

                      {this.state.isOpen && this.state.imageUrl !== null && (
                        <button
                          type="button"
                          onClick={this.retakePhoto}
                          className="btn-secondary w-full"
                        >
                          Retake Photo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Preview Section */}
                <div className="flex-1 min-w-[280px] flex flex-col items-center">
                  <div className="flex flex-col items-center justify-start p-3 w-full max-w-[350px]">
                    {this.state?.imageUrl !== null ? (
                      <>
                        <p className="border border-primary px-3 text-center mb-2 text-sm text-primary font-medium">
                          Selfie Preview - Confidence:{" "}
                          {(this.state.humanConfidence * 100).toFixed(0)}%
                        </p>
                        <img
                          src={this.state.imageUrl}
                          alt="Selfie Preview"
                          className="h-[14rem] w-[14rem] object-cover border-4 border-primary rounded-full overflow-hidden bg-gray-50"
                        />
                      </>
                    ) : (
                      <div className="h-[14rem] w-[14rem] flex flex-col items-center justify-center border-4 border-dashed border-primary rounded-full mb-3 bg-gray-50">
                        <p className="border border-primary px-3 text-center mb-2 text-sm text-primary">
                          Selfie Preview
                        </p>
                        <SquareUserRound className="h-28 w-28 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Section */}
              {this.state.isOpen && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={this.dataSubmit}
                    disabled={
                      this.state.imageUrl === null ||
                      this.state.humanConfidence < 0.6
                    }
                    className="btn-primary w-full max-w-xs mx-auto block"
                    title={
                      this.state.humanConfidence < 0.6
                        ? "Complete all liveness verification steps before submitting"
                        : ""
                    }
                  >
                    Submit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Global Loader */}
        {this.state.modelLoading && (
          <LoadingScreen
            message="Loading face detection model..."
            subMessage="Please wait while we initialize the model"
          />
        )}

        {/* API Submission Loader */}
        {this.state.isLoading && (
          <LoadingScreen
            message="Submitting your photo..."
            subMessage="Please wait while we process your verification"
          />
        )}
      </>
    );
  }
}

export default OwnPhotoUpload;
